import React, { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'
import { CHART_COLORS, twoDecimalFormat } from '../../library/helper'
import Download from '../Download'

/**
 * SimpleBarChart
 * Props:
 * - data: Array<{ year: number|string, value: number, hasData?: boolean, label?: string }>
 * - title?: string
 * - unitLabel?: string    // e.g. "kWh", "USD", "m³"
 * - chartId?: string
 * - chartHeight?: number
 * - seriesName?: string   // legend label for this single series
 * - colorIndex?: number   // pick from CHART_COLORS
 * - onDownloadImage?: (id: string, label: string) => void
 * - pdfLabel?: string
 * - showYearLabels?: boolean  // Show "No Data" label for years without data
 * - noDataLabel?: string      // Custom label for years without data
 */
function SimpleBarChart({
                            data = [
                                { year: 2023, value: 120 },
                                { year: 2024, value: 140 },
                                { year: 2025, value: 160 },
                            ],
                            title = 'Bar Chart',
                            unitLabel = '',
                            chartId = 'simpleBarChart',
                            chartHeight = 300,
                            seriesName = 'Value',
                            colorIndex = 0,
                            onDownloadImage = () => {},
                            pdfLabel = 'Bar Chart',
                            showYearLabels = false,
                            noDataLabel = 'No Data',
                        }) {
    const { i18n } = useTranslation()
    const locale = i18n.language === 'id' ? 'id-ID' : 'en-US'


    const { categories, values, dataPoints } = useMemo(() => {
        const rows = Array.isArray(data) ? [...data] : []
        rows.sort((a, b) => Number(a.year) - Number(b.year))
        return {
            categories: rows.map(r => String(r.year)),
            values: rows.map(r => Number(r.value ?? 0)),
            dataPoints: rows,
        }
    }, [data])

    const hasAnyData = values.some(v => v !== 0)

    const options = {
        chart: { id: chartId, type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 6 } },
        dataLabels: {
            enabled: true,
            formatter: (val, opts) => {
                const dataPointIndex = opts.dataPointIndex
                const dataPoint = dataPoints[dataPointIndex]
                
                if (showYearLabels && dataPoint?.hasData === false) {
                    return ''
                }
                
                return String(Math.round(val)).substring(0, 3)

            },
            style: { 
                fontSize: '11px',
                colors: ['#ffffff'] 
            },
        },
        xaxis: {
            categories,
            title: { text: 'Year' },
            labels: { rotate: -45 },
        },
        yaxis: {
            title: { text: unitLabel || '' },
            labels: { formatter: (val) => twoDecimalFormat(val, locale) },
        },
        tooltip: {
            y: {
                formatter: (val, ctx) => {
                    const dataPointIndex = ctx?.dataPointIndex ?? 0
                    const year = categories[dataPointIndex]
                    const dataPoint = dataPoints[dataPointIndex]
                    if (showYearLabels && dataPoint?.hasData === false) {
                        return `${noDataLabel} (${year})`
                    }
                    
                    return `${twoDecimalFormat(val, locale)}${unitLabel ? ` ${unitLabel}` : ''} (${year})`
                },
            },
        },
        grid: { strokeDashArray: 4 },
        legend: { show: true, position: 'top' },
        colors: [CHART_COLORS[colorIndex % CHART_COLORS.length]],
    }

    const series = [{ name: seriesName, data: values }]

    const onDownload = () => onDownloadImage(chartId, pdfLabel)

    return (
        <div
            className="bg-white d-flex flex-column gap-3 position-relative"
            style={{ borderRadius: 8, flex: 1 }}
            id={chartId}
        >
            <div className="align-items-start d-flex gap-3 justify-content-between pt-3 px-3 w-100">
                <p className={`fw-semibold m-0 ${!hasAnyData ? 'text-muted' : ''}`} style={{ fontSize: 12 }}>
                    {title}
                </p>
                <Download onClick={onDownload} isDisabled={!hasAnyData} />
            </div>

            <div className="px-3" style={{ position: 'relative' }}>
                {hasAnyData ? (
                    <div style={{ position: 'relative' }}>
                        <ReactApexChart options={options} series={series} type="bar" height={chartHeight} />
                        {showYearLabels && (
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '80px',
                                right: '30px',
                                transform: 'translateY(-90%)',
                                display: 'flex',
                                alignItems: 'center',
                                pointerEvents: 'none',
                            }}>
                                <div style={{
                                    display: 'flex',
                                    width: '100%',
                                    justifyContent: 'space-around',
                                }}>
                                    {dataPoints.map((dataPoint, index) => (
                                        <div
                                            key={`no-data-${index}`}
                                            style={{
                                                flex: 1,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {dataPoint?.hasData === false && (
                                                <span style={{
                                                    fontSize: '11px',
                                                    fontWeight: '400',
                                                    color: '#000000',
                                                }}>
                                                    {noDataLabel}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: chartHeight }}>
                        <p className="text-muted" style={{ fontSize: 12 }}>No Data</p>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SimpleBarChart